// frontend/src/components/players/PDFViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSecureMedia } from '../../services/mediaService';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDF_JS_OPTIONS = {
  disableAutoFetch: true,
  disableStream: false,
  useWorkerFetch: true,
  useSystemFonts: true,
  cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/standard_fonts/',
  maxImageSize: 512 * 1024,
  enableXfa: false,
  stopAtErrors: false,
  fontExtraProperties: false,
  isEvalSupported: false,
  useStandardFontData: true,
  verbosity: 0,
};

const QUALITY_LEVELS = {
  low: { label: 'اقتصادی', dpi: 100 },
  medium: { label: 'متوسط', dpi: 150 },
  high: { label: 'بالا', dpi: 200 },
  ultra: { label: 'عالی', dpi: 300 },
};

const PDFViewer = ({ mediaId, fileUrl, title }) => {
  const { mediaUrl, isLoading: urlLoading, error: urlError, refetch: refetchMediaUrl } =
    useSecureMedia(mediaId);

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [renderProgress, setRenderProgress] = useState(0);
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [isScannedPDF, setIsScannedPDF] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [quality, setQuality] = useState('medium');
  const [imageLoading, setImageLoading] = useState(false);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const loadingTaskRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isMounted = useRef(true);
  const pdfDocRef = useRef(null);

  const directPdf = fileUrl || mediaUrl;
  const proxyUrl = directPdf?.startsWith('https://s3.afra.kubit.cloud/')
    ? `https://farzaneh.app/api/proxy/media/?url=${encodeURIComponent(directPdf)}`
    : directPdf;

  // تشخیص نوع PDF
  useEffect(() => {
    if (!proxyUrl) return;
    isMounted.current = true;

    const detectPDFType = async () => {
      try {
        setLoading(true);
        setRenderProgress(10);
        
        const loadingTask = pdfjsLib.getDocument({ url: proxyUrl, ...PDF_JS_OPTIONS });
        loadingTaskRef.current = loadingTask;
        
        loadingTask.onProgress = (progress) => {
          if (isMounted.current) {
            const percent = Math.round((progress.loaded / progress.total) * 50);
            setRenderProgress(10 + percent);
          }
        };

        const pdf = await loadingTask.promise;
        if (!isMounted.current) return;
        
        pdfDocRef.current = pdf;
        
        const page = await pdf.getPage(1);
        const textContent = await page.getTextContent();
        const textLength = textContent.items.reduce((sum, item) => sum + item.str.length, 0);
        const isScanned = textLength < 50;
        
        setIsScannedPDF(isScanned);
        setNumPages(pdf.numPages);
        setRenderProgress(100);
        
        if (!isScanned) {
          setPdfDoc(pdf);
        } else {
          pdf.destroy();
          pdfDocRef.current = null;
        }
        
        setLoading(false);
        
      } catch (err) {
        console.error('Error:', err);
        if (isMounted.current) {
          setIsScannedPDF(false);
          setError('خطا در بارگذاری فایل');
          setLoading(false);
        }
      }
    };
    
    detectPDFType();

    return () => {
      isMounted.current = false;
      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
        loadingTaskRef.current = null;
      }
    };
  }, [proxyUrl]);

  // ✅ رندر Canvas - عرض محدود به parent
  useEffect(() => {
    if (!pdfDoc || isScannedPDF || !canvasRef.current) return;
    
    let cancelled = false;
    
    const render = async () => {
      try {
        if (renderTaskRef.current) {
          await renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const page = await pdfDoc.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        
        // عرض parent منهای padding
        const parentEl = canvasRef.current.parentElement;
        const cw = (parentEl?.clientWidth || 800) - 16; // padding
        const ch = window.innerHeight - 200;
        
        const scaleX = cw / viewport.width;
        const scaleY = ch / viewport.height;
        const finalScale = Math.max(0.5, Math.min(scaleX, scaleY, 2.0));
        
        const dpr = Math.min(window.devicePixelRatio || 1, 1); // DPR=1 برای جلوگیری از بزرگ شدن
        const renderScale = finalScale * dpr;

        const scaledViewport = page.getViewport({ scale: renderScale });
        const canvas = canvasRef.current;
        
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        
        // ✅ محدود کردن عرض به parent
        const displayWidth = Math.min(scaledViewport.width / dpr, cw);
        const ratio = displayWidth / (scaledViewport.width / dpr);
        const displayHeight = (scaledViewport.height / dpr) * ratio;
        
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto';
        canvas.style.maxWidth = '100%';

        const context = canvas.getContext('2d', { alpha: false });
        const renderTask = page.render({ canvasContext: context, viewport: scaledViewport, background: '#fff' });
        renderTaskRef.current = renderTask;
        
        await renderTask.promise;
        
        if (!cancelled) setScale(finalScale);
      } catch (err) {
        if (err.name !== 'RenderingCancelledException') console.error('Render error:', err);
      }
    };
    
    render();
    
    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, pageNumber]);

  // بارگذاری تصویر برای PDF اسکن شده
  const getImageUrl = useCallback((page, q = quality) => {
    if (!mediaId) return null;
    return `/api/v1/products/pdf-to-image/${mediaId}/?page=${page}&quality=${q}`;
  }, [mediaId, quality]);

  useEffect(() => {
    if (!isScannedPDF || numPages === 0) return;
    const url = getImageUrl(pageNumber, quality);
    if (url) {
      setImageUrl(url);
      setImageLoading(true);
    }
  }, [pageNumber, quality, numPages, isScannedPDF, getImageUrl]);

  // پیش‌بارگذاری
  useEffect(() => {
    if (!isScannedPDF || numPages === 0 || !mediaId) return;
    for (let i = pageNumber + 1; i <= Math.min(pageNumber + 2, numPages); i++) {
      const img = new Image();
      img.src = getImageUrl(i, quality);
    }
  }, [pageNumber, numPages, quality, mediaId, isScannedPDF, getImageUrl]);

  const changePage = useCallback((offset) => {
    setPageNumber(prev => Math.max(1, Math.min(prev + offset, numPages)));
  }, [numPages]);

  const goToPage = (page) => setPageNumber(Math.max(1, Math.min(page, numPages)));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));

  const cycleQuality = () => {
    const qualities = Object.keys(QUALITY_LEVELS);
    const idx = (qualities.indexOf(quality) + 1) % qualities.length;
    setQuality(qualities[idx]);
  };

  const handleContextMenu = (e) => e.preventDefault();
  const handleDragStart = (e) => e.preventDefault();

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) e.preventDefault();
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); changePage(1); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); changePage(-1); }
      if (isScannedPDF && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        const qs = Object.keys(QUALITY_LEVELS);
        const idx = qs.indexOf(quality);
        if (idx < qs.length - 1) setQuality(qs[idx + 1]);
      }
      if (isScannedPDF && e.key === '-') {
        e.preventDefault();
        const qs = Object.keys(QUALITY_LEVELS);
        const idx = qs.indexOf(quality);
        if (idx > 0) setQuality(qs[idx - 1]);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [changePage, quality, isScannedPDF]);

  useEffect(() => {
    return () => {
      if (renderTaskRef.current) renderTaskRef.current.cancel();
      if (pdfDocRef.current) pdfDocRef.current.destroy();
    };
  }, []);

  // ==================== UI ====================

  if (urlError || error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <p className="font-bold text-red-400 mb-4">{error || urlError}</p>
        <button onClick={() => { setError(null); refetchMediaUrl?.(); }} className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg">تلاش مجدد</button>
      </div>
    );
  }

  if (isScannedPDF === null || loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 mb-2">در حال بارگذاری...</p>
        <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-teal-500 transition-all duration-300" style={{ width: `${renderProgress}%` }} />
        </div>
        <p className="text-xs text-gray-400 mt-2">{renderProgress}%</p>
      </div>
    );
  }

  // PDF متنی - Canvas
  if (!isScannedPDF) {
    return (
      <div className="h-screen flex flex-col bg-gray-100 select-none" onContextMenu={handleContextMenu} onDragStart={handleDragStart} dir="ltr">
        <div className="flex flex-col bg-white border-b-2 border-teal-700 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-teal-600 truncate max-w-[150px] sm:max-w-xs">{title || 'کتاب'}</h2>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">📄 متنی</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-green-600">
              <span>صفحه {pageNumber} از {numPages}</span>
            </div>
          </div>
          <div className="flex items-center justify-between px-4 py-2 border-t border-teal-200 flex-wrap gap-2">
            <div className="flex items-center gap-1">
              <button onClick={zoomOut} className="p-1.5 rounded hover:bg-green-50 text-teal-600 border border-green-200">−</button>
              <span className="text-xs min-w-[45px] text-center text-teal-700">{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} className="p-1.5 rounded hover:bg-green-50 text-teal-600 border border-green-200">+</button>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="p-1.5 rounded hover:bg-green-50 text-teal-600 border border-teal-200 disabled:opacity-40">◀</button>
              <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => goToPage(parseInt(e.target.value) || 1)} className="w-12 px-1 py-1 bg-white text-green-700 text-center rounded border border-green-300 text-sm" />
              <span className="text-xs text-green-600">/ {numPages}</span>
              <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="p-1.5 rounded hover:bg-green-50 text-green-600 border border-green-200 disabled:opacity-40">▶</button>
            </div>
            <button onClick={() => { const el = document.querySelector('.pdf-viewer-container'); if (el?.requestFullscreen) el.requestFullscreen(); }} className="p-1.5 rounded hover:bg-green-50 text-green-600 border border-green-200">⛶</button>
          </div>
        </div>
        {/* ✅ Container با padding کم */}
        <div ref={containerRef} className="flex-1 w-full bg-gray-50 p-2 overflow-auto flex justify-center">
          <div className="bg-white rounded-lg shadow-xl" style={{ padding: '4px' }}>
            <canvas ref={canvasRef} />
          </div>
        </div>
      </div>
    );
  }

  // PDF اسکن شده - Loading
  if (imageLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 mb-2">در حال بارگذاری تصویر...</p>
        <p className="text-xs text-teal-600 mt-2">کیفیت: {QUALITY_LEVELS[quality]?.label} ({QUALITY_LEVELS[quality]?.dpi} DPI)</p>
      </div>
    );
  }

  // PDF اسکن شده - Display
  return (
    <div className="h-screen flex flex-col bg-gray-50 select-none" onContextMenu={handleContextMenu} onDragStart={handleDragStart} dir="ltr">
      <div className="bg-teal-800 text-white px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-1">
            <button onClick={cycleQuality} className={`px-2 py-1 text-xs rounded ${quality === 'ultra' ? 'bg-amber-600' : 'bg-gray-600'}`}>{QUALITY_LEVELS[quality]?.label}</button>
            <span className="text-xs bg-teal-700 px-2 py-0.5 rounded-full">{QUALITY_LEVELS[quality]?.dpi} DPI</span>
            <span className="text-xs bg-amber-600 px-2 py-0.5 rounded-full">📸 اسکن شده</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="p-1.5 rounded hover:bg-teal-700 disabled:opacity-40">◀</button>
            <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => goToPage(parseInt(e.target.value) || 1)} className="w-12 px-1 py-1 bg-white/20 text-white text-center rounded border border-white/30 text-sm" />
            <span className="text-xs opacity-75">/ {numPages}</span>
            <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="p-1.5 rounded hover:bg-teal-700 disabled:opacity-40">▶</button>
          </div>
          <button onClick={() => { const el = document.querySelector('.pdf-viewer-container'); if (el?.requestFullscreen) el.requestFullscreen(); }} className="p-1.5 rounded hover:bg-teal-700">⛶</button>
        </div>
      </div>
      <div ref={containerRef} className="flex-1 flex items-center justify-center bg-gray-50 p-2 overflow-auto">
        <div className="shadow-xl bg-white rounded-lg p-2">
          <img ref={imgRef} src={imageUrl} alt={`صفحه ${pageNumber}`} className="max-w-full h-auto" onLoad={() => setImageLoading(false)} onError={() => { setImageLoading(false); setError('خطا در بارگذاری تصویر'); }} />
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;